// Providers dropdown — clinical-only multi-select over the schemas in the
// user's users.schema column. Selection lives in useSelectedProviders (shared
// + persisted). Home is always checked and can't be unchecked. Styled to sit
// in the Schedules/Patients hero control row (inside #clinic-root).
import React, { useState, useEffect, useRef } from 'react';
import { useSelectedProviders } from '../hooks/useSelectedProviders';
import { hasClinicalAdminPermissions } from '../config/roles';

export default function ProvidersDropdown({ userRole }) {
  const { allSchemas, homeSchema, selected, toggle, isSelected } = useSelectedProviders();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Close on an outside click or Escape (standard dropdown dismissal). The
  // listeners attach only while open. Clicks on the trigger button or inside
  // the menu fall within containerRef, so they don't dismiss — multi-select
  // stays open until the user clicks away.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!hasClinicalAdminPermissions(userRole)) return null;
  if (!allSchemas || allSchemas.length <= 1) return null;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-md border border-slate-200/70 dark:border-slate-700/50 bg-white dark:bg-slate-800/40 px-3 h-8 text-sm font-bold tracking-tight text-slate-600 dark:text-slate-300 shadow-sm hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
      >
        <span aria-hidden="true" className="text-base leading-none">👥</span>
        Providers · {selected.length}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 z-30 w-60 rounded-xl border border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f] shadow-lg p-1.5">
          {allSchemas.map((s) => {
            const isHome = s === homeSchema;
            return (
              <label
                key={s}
                className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-sm ${
                  isHome ? 'opacity-70' : 'hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer'
                }`}
              >
                <input
                  type="checkbox"
                  className="accent-indigo-600"
                  checked={isSelected(s)}
                  disabled={isHome}
                  onChange={() => toggle(s)}
                />
                <span className="truncate">{isHome ? `${s} (home)` : s}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
